// @givmo/board — Board members › New member checklist: the board's own list for new members
// (CBG-YR4-START, g2; named plainly by CBG-UX6-COPY, design-268's g1).
//
// Before a board adopts a list, Board shows Givmo's starting list, built from what the board has
// RECORDED — its governing documents in force — plus the first conflict-of-interest disclosure,
// while the year's disclosures are on (and a board buddy, while the backend serves it: dark in every
// environment since CBG-YR4-BUDDY-DARK, so the card's line counts no steps, D-CBG-41). It is a
// suggestion until an officer presses "Use this checklist" (plan §3.6: a default is disclosed,
// reversible and adopted by the organization's own act): nothing is stored, and a member added before
// then gets no list. An instrument the board has not recorded is never listed: a list of what a board
// lacks would tell it what it ought to have (the governing-documents registry's Band-3 line).
//
// Adopting it, and changing it after, is the roster's own act (`board_member:manage`): later changes
// reach only members added afterwards, never a list already started.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isGivmoError, type BoardGettingStartedListItemOut } from '@givmo/api-client';
import { Button, Card, CardBody, Skeleton, toast } from '@givmo/design-system';
import { GroupLabel } from '../../components/fine-print';
import { hasBoardPermission, useBoardHost } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import { displayDate } from '../../lib/display';
import { shortDay } from '../conflicts/coi-decode';
import { ListEditorDialog, useDocumentChoices } from './onboarding-dialogs';
import { extractBoardList, itemTitle, titlesOf, toSpec, type ItemSpec } from './onboarding-decode';
import { onboardingWords as w } from './onboarding-words';

const LINE = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';
const SMALL = 'font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const ACT =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none hover:text-brand-700 focus-visible:underline';

function sourceLine(item: BoardGettingStartedListItemOut): string {
  if (item.item_kind === 'read')
    return item.available === false
      ? w.unavailable
      : item.governing_document_kind
        ? w.adoptedOn(displayDate(item.target_occurred_on))
        : w.recordLine(displayDate(item.target_occurred_on));
  if (item.item_kind === 'disclosure') return w.disclosureSource;
  if (item.item_kind === 'buddy') return w.buddySource;
  return w.stepSource;
}

function Rows({ items, testId }: { items: BoardGettingStartedListItemOut[]; testId: string }) {
  return (
    <ul className="flex flex-col" data-testid={testId}>
      {items.map((item, index) => (
        <li
          key={item.id ?? `${item.item_kind}-${index}`}
          className="flex flex-col gap-2 border-t border-border-subtle py-12 first:border-t-0 first:pt-0 last:pb-0"
          data-testid={`board-list-item-${item.position}`}
        >
          <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
            {itemTitle(item)}
          </span>
          <span className={SMALL}>{sourceLine(item)}</span>
        </li>
      ))}
    </ul>
  );
}

export function BoardListPart() {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const { orgRef, isReady, permissions } = useBoardHost();
  const canManage = hasBoardPermission(permissions, 'board_member:manage');
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listOptions = operations.officerGetBoardGettingStartedListOptions({
    path: { org_ref: orgRef ?? '' },
  });
  const listQuery = useQuery({
    ...listOptions,
    enabled: isReady && orgRef !== null,
    select: extractBoardList,
  });
  const { documents, failed: documentsFailed } = useDocumentChoices(editing);

  const save = useMutation({
    mutationFn: async ({ revision, items }: { revision: number | null; items: ItemSpec[] }) => {
      await operations.officerSaveBoardGettingStartedList({
        path: { org_ref: orgRef ?? '' },
        body: { revision, items },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      setEditing(false);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: listOptions.queryKey });
    },
    onError: (caught) => {
      const message = isGivmoError(caught)
        ? caught.message
        : 'We couldn’t save the list. Please try again.';
      if (editing) setError(message);
      else toast.error(message);
    },
  });

  const list = listQuery.data ?? null;
  if (listQuery.isPending) return <Skeleton className="h-84 w-full rounded-lg" />;
  if (list === null) return null;
  const items = list.items ?? [];
  const read = items.filter((item) => item.item_kind === 'read');
  const todo = items.filter((item) => item.item_kind !== 'read');
  const revision = list.adopted ? (list.revision ?? null) : null;

  return (
    <div className="flex flex-col gap-16" data-testid="board-getting-started">
      <p className="max-w-[72ch] font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {w.listLead}
      </p>
      <Card testId={list.adopted ? 'board-list-adopted' : 'board-list-starting'}>
        <CardBody className="flex flex-col gap-20">
          <div className="flex flex-col gap-4">
            <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              {list.adopted ? w.adoptedTitle : w.startingTitle}
            </h2>
            <p className={LINE}>
              {list.adopted
                ? w.adoptedLine(shortDay(list.adopted_at), list.adopted_by_display_name ?? null)
                : w.startingLine}
            </p>
          </div>
          {read.length > 0 ? (
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-2">
                <GroupLabel>{w.readGroup}</GroupLabel>
                <span className={SMALL}>{w.inForceSource}</span>
              </div>
              <Rows items={read} testId="board-list-read" />
            </div>
          ) : null}
          {todo.length > 0 ? (
            <div className="flex flex-col gap-8">
              <GroupLabel>{w.doGroup}</GroupLabel>
              <Rows items={todo} testId="board-list-todo" />
            </div>
          ) : null}
          {canManage ? (
            <div className="flex flex-wrap items-center gap-12">
              {!list.adopted ? (
                <Button
                  appearance="saas"
                  tone="primary"
                  loading={save.isPending && !editing}
                  onClick={() =>
                    save.mutate({ revision: null, items: items.map((item) => toSpec(item, false)) })
                  }
                  testId="board-list-adopt"
                >
                  {w.adoptList}
                </Button>
              ) : null}
              <button
                type="button"
                className={ACT}
                onClick={() => setEditing(true)}
                data-testid="board-list-edit"
              >
                {w.changeList}
              </button>
            </div>
          ) : null}
        </CardBody>
      </Card>
      {editing && documents !== undefined ? (
        <ListEditorDialog
          title={w.editTitleBoard}
          lead={w.editLeadBoard}
          initial={items.map((item) => toSpec(item, false))}
          titles={titlesOf(items)}
          documents={documents}
          documentsFailed={documentsFailed}
          pending={save.isPending}
          error={error}
          onCancel={() => {
            setEditing(false);
            setError(null);
          }}
          onSave={(next) => save.mutate({ revision, items: next })}
        />
      ) : null}
    </div>
  );
}
